/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars, react/no-unescaped-entities */
import React, { useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { acceptOrganizationInvite } from './api';
import { AppShell } from './components/shell/AppShell';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { SessionProvider } from './contexts/SessionContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { AuthPage } from './pages/auth/AuthPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { OnboardingPage } from './pages/auth/OnboardingPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { RepoLinkOauthCallbackPage } from './pages/RepoLinkOauthCallbackPage';
import { buildWorkspaceModulePath } from './utils/routes';
import { buildWorkspacePath, readRouteFromPath } from './utils/routes';
import { isWorkspaceScopedPage } from './config/pages';
import { useSession } from './contexts/SessionContext';
import { useWorkspace } from './contexts/WorkspaceContext';

// ── Inner app — rendered inside both providers ────────────────────────────────
function AppInner() {
  const navigate = useNavigate();
  const handleNavigate = useCallback(
    (path: string, opts?: { replace?: boolean }) => navigate(path, opts),
    [navigate]
  );
  const location = useLocation();
  const { session, sessionLoading, signOut } = useSession();
  const {
    workspace,
    workspaceLoading,
    workspaceError,
    hasLoadedWorkspaceOnce,
    workspaceBootstrapOnly,
    activeOrganization,
    activeProject,
    activeOrgId,
    activeProjectId,
    setActiveOrgId,
    setActiveProjectId,
    setActivePage,
    setWorkspaceVersion,
    refreshWorkspace
  } = useWorkspace();

  const { organizations, apps } = workspace;

  const route = readRouteFromPath(location.pathname);
  const routeKind = route.kind;
  const routeOrgSlug = route.kind === 'workspace' ? route.orgSlug : '';
  const routeProjectSlug = route.kind === 'workspace' ? route.projectSlug : '';
  const routePageId = route.kind === 'workspace' ? route.pageId : 'overview';

  useEffect(() => {
    if (routeKind !== 'workspace') return;

    setActivePage(routePageId);

    const routedOrganization = routeOrgSlug
      ? organizations.find((organization) => organization.slug === routeOrgSlug)
      : undefined;
    if (routedOrganization && routedOrganization.id !== activeOrgId) {
      setActiveOrgId(routedOrganization.id);
      setActiveProjectId('');
      return;
    }

    const routedProject = routeProjectSlug
      ? apps.find(
          (project) =>
            project.slug === routeProjectSlug &&
            (!routedOrganization ||
              project.organizationId === routedOrganization.id)
        )
      : undefined;
    if (routedProject && routedProject.id !== activeProjectId) {
      setActiveProjectId(routedProject.id);
    }
  }, [
    activeOrgId,
    activeProjectId,
    apps,
    organizations,
    routeKind,
    routeOrgSlug,
    routePageId,
    routeProjectSlug,
    setActiveOrgId,
    setActivePage,
    setActiveProjectId
  ]);

  useEffect(() => {
    if (
      routeKind === 'workspace' &&
      isWorkspaceScopedPage(routePageId) &&
      routeOrgSlug &&
      routeProjectSlug
    ) {
      navigate(buildWorkspaceModulePath(routeOrgSlug, routePageId), {
        replace: true
      });
      return;
    }

    if (
      routeKind !== 'workspace' ||
      !routeProjectSlug ||
      routeProjectSlug === 'switching' ||
      organizations.length === 0 ||
      apps.length === 0
    ) {
      return;
    }

    const routedOrganization = organizations.find(
      (organization) => organization.slug === routeOrgSlug
    );
    const routeExists = apps.some(
      (project) =>
        project.slug === routeProjectSlug &&
        (!routedOrganization ||
          project.organizationId === routedOrganization.id)
    );
    if (routeExists) return;

    const canonicalOrganization =
      routedOrganization ?? activeOrganization ?? organizations[0];
    const canonicalProject =
      apps.find(
        (project) =>
          project.id === activeProjectId &&
          project.organizationId === canonicalOrganization.id
      ) ??
      apps.find(
        (project) => project.organizationId === canonicalOrganization.id
      );
    if (canonicalProject) {
      navigate(
        buildWorkspacePath(
          canonicalOrganization.slug,
          canonicalProject.slug,
          routePageId
        ),
        { replace: true }
      );
    }
  }, [
    activeOrganization,
    activeProjectId,
    apps,
    navigate,
    organizations,
    routeKind,
    routeOrgSlug,
    routePageId,
    routeProjectSlug
  ]);

  const isBootstrapWorkspace = workspaceBootstrapOnly;
  const requiresOnboarding = Boolean(
    session &&
      hasLoadedWorkspaceOnce &&
      !workspaceLoading &&
      organizations.length > 0 &&
      apps.length === 0
  );

  // Update document title
  useEffect(() => {
    const pageTitle = activeOrganization?.name || 'Cindro Admin';
    document.title = pageTitle;
  }, [activeOrganization?.name]);

  useEffect(() => {
    if (!session) {
      Sentry.setUser(null);
      Sentry.setContext('workspace', null);
      return;
    }

    Sentry.setUser({
      id: session.user.tenantUserId,
      email: session.user.email,
      username: session.user.displayName
    });
    Sentry.setTags({
      organization_slug: activeOrganization?.slug || 'unselected',
      project_slug: activeProject?.slug || 'workspace',
      cindro_page: routePageId,
      tenant_role: session.user.role
    });
    Sentry.setContext('workspace', {
      organizationId: activeOrganization?.id || null,
      organizationName: activeOrganization?.name || null,
      projectId: activeProject?.id || null,
      projectName: activeProject?.name || null,
      page: routePageId
    });
  }, [activeOrganization, activeProject, routePageId, session]);

  if (sessionLoading) {
    return (
      <div className="authShell">
        <div className="authCard">
          <div className="authKicker">Signing in</div>
          <h1>Opening Cindro</h1>
          <p>Checking your account.</p>
        </div>
      </div>
    );
  }

  if (route.kind === 'forgot-password') {
    return <ForgotPasswordPage />;
  }

  if (route.kind === 'reset-password') {
    return <ResetPasswordPage token={route.token} />;
  }

  if (route.kind === 'super-admin') {
    if (!session) {
      navigate('/login?redirect=%2Fsuper-admin', { replace: true });
      return null;
    }
    if (!session.user.isSuperAdmin) {
      return (
        <div className="authShell">
          <div className="authCard">
            <div className="authKicker">Access Denied</div>
            <h1>Not a super admin</h1>
            <p>Your account does not have super admin access.</p>
          </div>
        </div>
      );
    }
    return <SuperAdminPage />;
  }

  if (route.kind === 'repo-link-callback') {
    if (!session) {
      navigate('/login', { replace: true });
      return null;
    }
    return (
      <RepoLinkOauthCallbackPage
        verificationToken={route.verificationToken}
        error={route.error}
        onNavigate={(path, opts) => navigate(path, opts)}
      />
    );
  }

  if (route.kind === 'accept-invite') {
    return (
      <AcceptInvitePage
        token={route.token}
        session={session}
        onAccepted={(_organizationId) => {
          setWorkspaceVersion((v) => v + 1);
          navigate('/onboarding', { replace: true });
        }}
        onSignIn={() => {
          navigate(
            `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`,
            { replace: true }
          );
        }}
        onSignUp={() => {
          navigate(
            `/signup?redirect=${encodeURIComponent(location.pathname + location.search)}`,
            { replace: true }
          );
        }}
        acceptFn={async (token) => {
          if (!session) throw new Error('Not logged in');
          return acceptOrganizationInvite(session.accessToken, token);
        }}
      />
    );
  }

  if (!session) {
    return (
      <AuthPage
        initialMode={route.kind === 'auth' ? route.authMode : 'login'}
      />
    );
  }

  if (workspaceError && !hasLoadedWorkspaceOnce) {
    return (
      <div className="authShell">
        <div className="authCard">
          <div className="authKicker">Could not open workspace</div>
          <h1>Try loading it again</h1>
          <p>{workspaceError} Your account is still signed in.</p>
          <div className="authRecoveryActions">
            <button
              type="button"
              className="primaryButton"
              onClick={refreshWorkspace}
            >
              Try again
            </button>
            <button type="button" className="secondaryButton" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Only block the whole app behind a full-screen loader on the very first
  // workspace fetch. Subsequent loads (org switch, project switch, manual
  // refresh) keep the shell mounted and show a slim top-of-page indicator
  // instead — losing the sidebar on every switch reads as broken.
  if (
    !hasLoadedWorkspaceOnce &&
    (workspaceLoading ||
      (organizations.length > 0 &&
        apps.length > 0 &&
        (!activeOrganization || !activeProject)))
  ) {
    return (
      <div className="authShell">
        <div className="authCard">
          <div className="authKicker">Workspace</div>
          <h1>Opening your workspace</h1>
          <p>Loading projects, releases, and work that needs attention.</p>
        </div>
      </div>
    );
  }

  if (
    route.kind === 'onboarding' ||
    isBootstrapWorkspace ||
    requiresOnboarding ||
    (session && organizations.length === 0)
  ) {
    return <OnboardingPage />;
  }

  return <AppShell onNavigate={handleNavigate} />;
}

// ── Root app — provides session and workspace context ─────────────────────────
function App() {
  const navigate = useNavigate();
  const handleNavigate = useCallback(
    (path: string, opts?: { replace?: boolean }) => navigate(path, opts),
    [navigate]
  );

  return (
    <FeedbackProvider>
      <SessionProvider onNavigate={handleNavigate}>
        <WorkspaceProvider onNavigate={handleNavigate}>
          <AppInner />
        </WorkspaceProvider>
      </SessionProvider>
    </FeedbackProvider>
  );
}

export { App };
